import React from 'react';

import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useBaseTable } from 'hooks/useBaseTable';

import { PeriodItemEntry } from 'api/queries/getSummaryTrackedActivities.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { CustomizedStatusBar } from 'components/common/CustomizedStatusBar/CustomizedStatusBar';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import {
    IHashmap,
    ITableWidgetWrapperProps,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import tableClasses from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { columnsV8, initialState } from 'components/widgets/myTrackedActivities/myTrackedActivities.utils';
import { useMyTrackedActivitiesWidgetLogic } from 'components/widgets/myTrackedActivities/myTrackedActivitiesWidget.logic';
import classes from 'components/widgets/myTrackedActivities/myTrackedActivitiesWidget.module.scss';
import { useMyTrackedActivitiesWidgetProvider } from 'components/widgets/myTrackedActivities/myTrackedActivitiesWidget.provider';

const title = 'My Tracked Activities';
const id = 'summary-tracked-activities';
const componentKey = 'myTrackedActivities';

export const MyTrackedActivitiesWidget = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
}: ITableWidgetWrapperProps) => {
    const { table } = useBaseTable({
        widgetId: null,
        localFilters: [],
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { api, ui } = useMyTrackedActivitiesWidgetProvider({
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,
    });

    const { logic } = useMyTrackedActivitiesWidgetLogic({
        id,
        api,
        ui,
    });

    return (
        <WidgetWithTitle
            title={
                <WidgetTitle
                    title={title}
                    postfix={
                        <>
                            {logic.activeData?.length && `(${logic.activeData?.length})`}
                            <div className={classes.statusBar}>
                                <div className={classes.statusBarWrapper}>
                                    <CustomizedStatusBar data={logic.statusBarData} />
                                </div>
                            </div>
                        </>
                    }
                />
            }
            id={id}
            titleComponents={[
                <ExportDropdown title={title} key={'export'} subTitle={logic.subTitle} />,
                <ColumnsManager
                    onOpenModal={() => table.setModalColumnsOpen(true)}
                    onHideModal={() => table.setModalColumnsOpen(false)}
                    showModal={table.modalColumnsOpen}
                    componentKey={componentKey as keyof IHashmap}
                    tableInstance={table.tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                />,
                <TooltipIcon key={'info'} tooltip={api.getSummaryTrackedActivities.data?.info} />,
                <ThreeDotsMenu componentKey={componentKey} shareTitle={title} shareLink={logic.route} />,
            ]}
        >
            <TableContainer className={tableClasses.tableWrapperContainer}>
                {table.updatedInitialState !== undefined && (
                    <TableV8<PeriodItemEntry>
                        maxHeight={500}
                        data={logic.activeData}
                        columns={columnsV8}
                        isLoading={
                            api.getSummaryTrackedActivities.isLoading || api.getSummaryTrackedActivities.isFetching
                        }
                        noData={
                            <NoData>
                                No tracked activities added yet. Find and add via{' '}
                                <Link
                                    to={AppRoutes.buildPath(
                                        AppRoutes.DASHBOARD_EXECUTION_PATH,
                                        executionConfig.battlecards.link,
                                    )}
                                >
                                    {executionConfig.battlecards.title}
                                </Link>
                            </NoData>
                        }
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualPagination={false}
                        manualSorting={false}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                    />
                )}
            </TableContainer>
        </WidgetWithTitle>
    );
};
